import { adoptLocalValue, safeLocalStorage, siteCookieStore, type KeyValueStore } from "./siteStorage";

const STORAGE_KEY = "act.guestId.v1";

/** Where the guest id lives: a cookie for all of actuniverse.com on an ACT
 *  site, so the hub and every game show the same guest label (the id from
 *  play.actuniverse.com's old localStorage is carried over first); plain
 *  localStorage anywhere else, or where cookies are blocked. */
function guestIdStore(): KeyValueStore {
  const local = safeLocalStorage();
  const shared = siteCookieStore();
  if (!shared) {
    if (!local) throw new Error("no storage");
    return local;
  }
  // Only the old one-origin site holds a local id, and it predates any shared one.
  if (local) adoptLocalValue(STORAGE_KEY, shared, local, () => "local");
  return shared;
}

/**
 * Stable guest identity (end-goal overview: guests play immediately with a
 * temporary profile). Survives reloads (and, on an ACT site, subdomains);
 * wallet-bound identity replaces this through the same seam later.
 */
export function getOrCreateGuestId(): string {
  // Asked for on every chat line; the id never changes within a page's life.
  if (cachedId) return cachedId;
  try {
    const store = guestIdStore();
    const existing = store.getItem(STORAGE_KEY);
    if (existing) return (cachedId = existing);
    const id = `guest.${crypto.randomUUID()}`;
    store.setItem(STORAGE_KEY, id);
    return (cachedId = id);
  } catch {
    // Storage unavailable (private mode edge cases) — session-only identity.
    return (cachedId = `guest.session.${Math.floor(Math.random() * 1e9)}`);
  }
}

let cachedId: string | null = null;

export function shortGuestLabel(guestId: string): string {
  return `guest-${guestId.slice(-4)}`;
}
